import { HEALTH_ROUTE, healthResponseSchema } from '@kenocyber/shared';
import { API_BASE_URL } from '../auth/client';

/**
 * Is the service serving, and if not, why?
 *
 * ## One place, three answers, never mixed
 *
 * `maintenance` is a transition somebody started and will finish.
 * `stage_mismatch` is a deployment landing: nobody chose it and no player
 * action ends it. `unreachable` is the network or the server being gone.
 *
 * They get different screens because they are different facts. Calling an
 * outage "maintenance" tells a player their data is being rewritten when
 * nothing is being rewritten, and it hides the one case where retrying
 * immediately is the right move.
 *
 * ## Where the answer comes from
 *
 * Two sources, and both are the SERVER's:
 *
 * - `GET /health`, asked once before anything mounts. It is public, read-only
 *   and carries one word.
 * - any authoritative `503` a route answers mid-session, reported here by the
 *   API client. That path costs no extra request, and it catches the case a
 *   startup probe structurally cannot: maintenance that begins while somebody
 *   is playing.
 *
 * ## What it deliberately does not do
 *
 * No polling while the game is serving. The state changes a few times a year,
 * and a client that asked every few seconds would be a client that asks a
 * million times for every time it matters. While a notice is on screen the
 * check repeats at most every 15 s, and the button asks immediately.
 */
export type ServiceState = 'serving' | 'maintenance' | 'stage_mismatch' | 'unreachable';

/** The state is not serving, and the player is looking at a screen about it. */
export function isOutage(state: ServiceState): boolean {
  return state !== 'serving';
}

/**
 * How long `/health` gets to answer before it counts as unreachable.
 *
 * A request that never resolves would leave the player on a spinner for ever,
 * which is the one outcome that says nothing at all.
 */
export const HEALTH_TIMEOUT_MS = 8_000;

/** The floor between automatic re-checks while a notice is on screen. */
export const RECHECK_INTERVAL_MS = 15_000;

/**
 * Asks the server how it is serving. Never throws.
 *
 * Anything that is not a clean, parseable, `ok` answer is `unreachable`: a
 * gateway's HTML error page, a truncated body, a DNS failure and a timeout are
 * all the same fact from here, and none of them is a transition.
 *
 * An API that answers without the `release` field is an older deployment that
 * predates it. It is serving - that field's absence has never meant anything
 * else - and inventing an outage from a missing optional would shut a working
 * game.
 */
export async function probeService(now: () => Date = () => new Date()): Promise<{
  readonly state: ServiceState;
  readonly at: Date;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}${HEALTH_ROUTE}`, {
      // `/health` is public and carries no session. Sending the cookie would
      // add nothing and widen what a health check can touch.
      credentials: 'omit',
      cache: 'no-store',
      signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS),
    });
    if (!response.ok) {
      return { state: 'unreachable', at: now() };
    }
    const parsed = healthResponseSchema.parse(await response.json());
    return { state: parsed.release ?? 'serving', at: now() };
  } catch {
    // Deliberately silent. The screen says what is true - the server did not
    // answer - and a console full of the same failure helps nobody.
    return { state: 'unreachable', at: now() };
  }
}

/* -------------------------------------------------------------------------- */
/*  Outages reported from inside a running session                            */
/* -------------------------------------------------------------------------- */

type Listener = (state: ServiceState) => void;

const listeners = new Set<Listener>();
let reported: ServiceState = 'serving';

/**
 * Called by the API client when an authoritative route answers `503`.
 *
 * Synchronous on purpose: the moment a request is refused, every subscriber
 * knows, in the same tick. Waiting for a `/health` round trip would leave the
 * arena mounted and simulating for as long as that took - which is precisely
 * the state this exists to end.
 *
 * The report is a fact about the LAST answer, not a latch: `clearReported`
 * takes it back when a probe finds the service serving again.
 */
export function reportServiceOutage(state: ServiceState): void {
  if (state === reported) {
    return;
  }
  reported = state;
  for (const listener of listeners) {
    listener(state);
  }
}

/** Forgotten when a probe says the service is back. */
export function clearReportedOutage(): void {
  reported = 'serving';
}

export function reportedServiceState(): ServiceState {
  return reported;
}

export function subscribeServiceState(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
